import { useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { ACTIVATION_KINDS } from 'definitions';
import { useExperiment } from './useExperiment';

export type ActivationKind = (typeof ACTIVATION_KINDS)[number];

type AddActivationParams = { kind: ActivationKind; value: string };

export const useActivationTargeting = (experimentId: number) => {
  const { experiment } = useExperiment(experimentId);
  const queryClient = useQueryClient();
  const apiClient = useClient();

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] });

  // experimentId rides in the query string: canExperiment authorises the experiment it finds
  // there, and the API only deletes a rule that belongs to it.
  const deleteActivationTargeting = async (id: number) => {
    await apiClient.delete(`/activation-targeting/${id}?experimentId=${experimentId}`);
    await refresh();
  };

  const addActivationTargeting = async ({ kind, value }: AddActivationParams) => {
    await apiClient.post('/activation-targeting', { kind, value, experimentId });
    await refresh();
  };

  return {
    activationTargeting: experiment?.activationTargeting ?? [],
    // A redirect fires inside the page code that sent the event, so the API refuses rules on it.
    activationAvailable: experiment?.type !== 'URL Redirect',
    addActivationTargeting,
    deleteActivationTargeting,
  };
};
